import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { buscarGradePicks } from '../lib/negocio'

export default function Grade() {
  const { membroAtual, ligaAtual } = useAuth()
  const [semanas, setSemanas] = useState([])
  const [grade, setGrade] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ligaAtual?.id) return
    async function carregar() {
      setLoading(true)
      const { semanas: s, grade: g } = await buscarGradePicks(ligaAtual.id)
      // Garante sempre 22 colunas
      const tiposExtra = ['wildcard', 'divisional', 'conference', 'superbowl']
      const semanasCompletas = [...(s || [])]
      const tiposExistentes = semanasCompletas.map(sem => sem.tipo)
      tiposExtra.forEach(tipo => {
        if (!tiposExistentes.includes(tipo)) {
          semanasCompletas.push({ id: `placeholder_${tipo}`, numero: null, tipo, picks_revelados: false, placeholder: true })
        }
      })
      // Garante S1-S18
      for (let n = 1; n <= 18; n++) {
        if (!semanasCompletas.find(s => s.tipo === 'regular' && s.numero === n)) {
          semanasCompletas.push({ id: `placeholder_s${n}`, numero: n, tipo: 'regular', picks_revelados: false, placeholder: true })
        }
      }
      semanasCompletas.sort((a, b) => {
        const ordem = { regular: 0, wildcard: 1, divisional: 2, conference: 3, superbowl: 4 }
        if (ordem[a.tipo] !== ordem[b.tipo]) return ordem[a.tipo] - ordem[b.tipo]
        return (a.numero || 0) - (b.numero || 0)
      })
      setSemanas(semanasCompletas)
      setGrade(g)
      setLoading(false)
    }
    carregar()
  }, [ligaAtual?.id])

  function labelSemana(sem) {
    if (sem.tipo === 'regular') return `S${sem.numero}`
    const siglas = { wildcard: 'WC', divisional: 'DIV', conference: 'CONF', superbowl: 'SB' }
    return siglas[sem.tipo] || sem.tipo.slice(0, 2).toUpperCase()
  }

  function celula(pick, semPlaceholder) {
    if (semPlaceholder) return <span style={s.gPend}>·</span>
    if (!pick) return <span style={s.gPend}>·</span>
    if (pick.resultado === 'win') return <span style={s.gWin}>{pick.time || '✓'}</span>
    if (pick.resultado === 'loss') return <span style={s.gLoss}>{pick.time || '✗'}</span>
    if (pick.resultado === 'draw') return <span style={s.gDraw}>{pick.time || '='}</span>
    if (pick.resultado === 'nopick') return <span style={s.gNoPick}>—</span>
    if (pick.time === '?') return <span style={s.gPend}>?</span>
    return <span style={s.gPend}>{pick.time}</span>
  }

  if (loading) return <div style={{ fontSize: 13, color: '#888', padding: '1.5rem 0', textAlign: 'center' }}>Carregando grade...</div>

  // Só mostra semanas já reveladas (oculta futuras e placeholders)
  const semanasVisiveis = semanas.filter(sem => !sem.placeholder && sem.picks_revelados)

  // Calcula semanas sobrevividas por membro (picks com resultado revelado)
  const semanasSobreviveu = (picks) => Object.values(picks).filter(p => p.resultado !== null).length

  // Ordena: vidas desc → semanas sobrevividas desc → spread_medio desc → vidas_medias desc
  const participantes = Object.entries(grade).sort(([, a], [, b]) => {
    if ((b.vidas ?? -99) !== (a.vidas ?? -99)) return (b.vidas ?? -99) - (a.vidas ?? -99)
    const semA = semanasSobreviveu(a.picks), semB = semanasSobreviveu(b.picks)
    if (semB !== semA) return semB - semA
    if ((b.spreadMedio ?? -99) !== (a.spreadMedio ?? -99)) return (b.spreadMedio ?? -99) - (a.spreadMedio ?? -99)
    return (b.vidasMedias ?? -99) - (a.vidasMedias ?? -99)
  })

  return (
    <div>
      <div style={s.banner}>
        Grade completa · verde=vitória · vermelho=derrota/sem pick · laranja=empate · ? = aguardando
      </div>

      {participantes.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '2rem 0' }}>
          Nenhum participante encontrado.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, textAlign: 'left', minWidth: 140, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                  Participante
                </th>
                {semanasVisiveis.map(sem => (
                  <th key={sem.id} style={s.th}>
                    {labelSemana(sem)}
                  </th>
                ))}
                <th style={{ ...s.th, borderLeft: '1px solid #eee' }}>Vidas</th>
                <th style={s.th}>Sobreviveu Até</th>
                <th style={s.th}>Spread Méd.</th>
                <th style={s.th}>Vidas Méd.</th>
              </tr>
            </thead>
            <tbody>
              {participantes.map(([pid, dados], idx) => (
                <tr key={pid}>
                  <td style={{ ...s.td, fontWeight: 500, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                    <span style={s.pos}>{idx + 1}</span>
                    {dados.nome}
                    {pid === String(membroAtual?.id) && <span style={s.youTag}>você</span>}
                  </td>
                  {semanasVisiveis.map(sem => (
                    <td key={sem.id} style={{ ...s.td, textAlign: 'center' }}>
                      {celula(dados.picks[sem.id], sem.placeholder)}
                    </td>
                  ))}
                  <td style={{ ...s.td, textAlign: 'center', borderLeft: '1px solid #eee', fontWeight: 600, color: '#0A2342' }}>
                    {dados.vidas ?? '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', color: '#555' }}>
                    {semanasSobreviveu(dados.picks)}sem
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', color: '#555' }}>
                    {dados.spreadMedio != null ? Number(dados.spreadMedio).toFixed(2) : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', color: '#555' }}>
                    {dados.vidasMedias != null ? Number(dados.vidasMedias).toFixed(2) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const s = {
  banner: { background: '#EAF3F8', border: '0.5px solid #378ADD', borderRadius: 8, padding: '8px 12px', marginBottom: '.9rem', fontSize: 12, color: '#1A5C9A' },
  table: { borderCollapse: 'collapse', whiteSpace: 'nowrap', fontSize: 11 },
  th: { color: '#888', padding: '4px 5px', textAlign: 'center', fontWeight: 500, borderBottom: '0.5px solid #eee' },
  td: { padding: '3px 5px', borderBottom: '0.5px solid #eee' },
  pos: { color: '#bbb', fontSize: 10, fontWeight: 400, minWidth: 22, display: 'inline-block' },
  youTag: { fontSize: 10, background: '#FAECE7', color: '#993C1D', borderRadius: 99, padding: '1px 5px', marginLeft: 5 },
  gWin: { background: '#EAF3DE', color: '#3B6D11', borderRadius: 3, padding: '1px 4px', fontSize: 10 },
  gLoss: { background: '#FCEBEB', color: '#A32D2D', borderRadius: 3, padding: '1px 4px', fontSize: 10 },
  gDraw: { background: '#FAEEDA', color: '#633806', borderRadius: 3, padding: '1px 4px', fontSize: 10 },
  gNoPick: { background: '#FCEBEB', color: '#A32D2D', borderRadius: 3, padding: '1px 4px', fontSize: 10 },
  gPend: { color: '#ccc', fontSize: 10 },
}
